/**
 * Text Only Content Slide
 * Displays rich text content with a customizable background color
 */

import type { ContentSlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

const RICH_TEXT_STYLES = `
  .rich-text-content h1 {
    font-size: 2em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h3 {
    font-size: 1.25em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content strong {
    font-weight: bold;
  }
  .rich-text-content em {
    font-style: italic;
  }
  .rich-text-content u {
    text-decoration: underline;
  }
  .rich-text-content ul {
    list-style-type: disc;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content ol {
    list-style-type: decimal;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content li {
    margin-bottom: 0.5em;
  }
  .rich-text-content p {
    margin-bottom: 1em;
  }
  .rich-text-content .ql-align-center {
    text-align: center;
  }
  .rich-text-content .ql-align-right {
    text-align: right;
  }
  .rich-text-content .ql-align-left {
    text-align: left;
  }
  .rich-text-content .ql-align-justify {
    text-align: justify;
  }
`;

/**
 * Returns true if the background color is dark, so we can use light text.
 */
function isDarkColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Perceived brightness formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 140;
}

export default function TextOnlySlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;
  const bgColor = content?.backgroundColor || '#ffffff';
  const useLightText = isDarkColor(bgColor);

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      <style>{RICH_TEXT_STYLES}</style>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}
      <div
        className="rounded-2xl p-8 md:p-12 min-h-[300px] flex items-center justify-center shadow-sm border border-border"
        style={{ backgroundColor: bgColor }}
      >
        {content?.content ? (
          <div className="prose prose-sm max-w-none w-full">
            <div
              className={`text-lg leading-relaxed rich-text-content ${useLightText ? 'text-white' : 'text-foreground'}`}
              dangerouslySetInnerHTML={{ __html: content.content }}
            />
          </div>
        ) : (
          <p className={`text-lg italic ${useLightText ? 'text-white/60' : 'text-muted-foreground'}`}>
            No content added yet
          </p>
        )}
      </div>
    </div>
  );
}
